import { useCallback, useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuthStore } from "../store/auth.store";
import { pendingWorkService } from "../services/pendingWork.service";
import type { PendingWorkFilters, PendingWorkView } from "../types/pendingWork";

export function usePendingWorkSummary() {
  const userId = useAuthStore((state) => state.profile?.id);
  return useQuery({
    queryKey: ["pending-work-summary", userId],
    queryFn: ({ signal }) => pendingWorkService.summary(signal),
    enabled: Boolean(userId),
  });
}
export function usePendingWorkCount(
  filters: PendingWorkFilters,
  enabled: boolean
) {
  const userId = useAuthStore((state) => state.profile?.id);
  return useQuery({
    queryKey: ["pending-work-count", userId, filters],
    queryFn: ({ signal }) => pendingWorkService.summary(signal, filters),
    enabled: enabled && Boolean(userId),
  });
}
export function usePendingWork(view: PendingWorkView, enabled: boolean) {
  const userId = useAuthStore((state) => state.profile?.id);
  return useInfiniteQuery({
    queryKey: ["pending-work", userId, view.filters, view.sorts],
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      pendingWorkService.list(view.filters, view.sorts, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled: enabled && Boolean(userId),
  });
}
export function usePendingWorkDetails(rootId: string) {
  const userId = useAuthStore((state) => state.profile?.id);
  return useInfiniteQuery({
    queryKey: ["pending-work-details", userId, rootId],
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      pendingWorkService.details(rootId, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled: Boolean(userId),
  });
}

export function usePendingWorkDetailsBatch(rootIds: string[]) {
  const userId = useAuthStore((state) => state.profile?.id);
  return useQuery({
    queryKey: ["pending-work-details", userId, rootIds],
    queryFn: ({ signal }) => pendingWorkService.detailsBatch(rootIds, signal),
    enabled: Boolean(userId) && rootIds.length > 0,
  });
}

export function usePendingWorkScroll(load: () => void, enabled: boolean) {
  const observer = useMemo(
    () => ({ current: undefined as IntersectionObserver | undefined }),
    []
  );
  return useCallback(
    (node: HTMLDivElement | null) => {
      observer.current?.disconnect();
      if (!node || !enabled) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) load();
        },
        { rootMargin: "250px" }
      );
      observer.current.observe(node);
    },
    [enabled, load, observer]
  );
}
